/* eslint-disable import/prefer-default-export */
import cloneDeep from 'lodash.clonedeep';
import ipRegex from 'ip-regex';
import { Network } from 'data/network/network.types';
import { MediaGateway, AdaptedMediaGateway } from 'data/mediaGateway/mediaGateway.types';
import {
  FormTemplate,
  FormTemplateField,
  FormTemplateOption,
  FormTemplateSection,
} from 'data/template/template.types';

export type ListenersIpAddressPortsType = {
  [key: string]: string[],
}

export function adaptTemplateVisibilityMode(
  template: FormTemplate | undefined,
  mode: 'create' | 'edit' | 'view' | 'copy',
): FormTemplate | undefined {
  if (template == null) {
    return;
  }

  const clonedTemplate = cloneDeep(template);

  if (mode === 'edit') {
    (clonedTemplate.main as FormTemplateSection).fields.id.readOnly = true;
  } else if (mode === 'view') {
    setAllFieldsAsReadOnlyExcludingFields(clonedTemplate, []);
  }

  return clonedTemplate;
}

export function adaptTemplateMediagatewaynumlist(
  template: FormTemplate | undefined,
  mediaGateway: MediaGateway,
): FormTemplate | undefined {
  const clonedTemplate = cloneDeep(template);

  if (mediaGateway && clonedTemplate) {
    const templateSectionMainFields = (clonedTemplate.main as FormTemplateSection)?.fields;
    if (templateSectionMainFields?.mediagatewaynumlist) {
      templateSectionMainFields.mediagatewaynumlist.tags = mediaGateway.mediagatewaynumlist.map((mediagatewaynum) => {
        return mediagatewaynum?.callingnum;
      })
    }
  }

  return clonedTemplate;
}

export function adaptMediaGatewayData(
  mediaGatewayData: MediaGateway,
): AdaptedMediaGateway {
  const clonedMediaGatewayData: AdaptedMediaGateway = cloneDeep({
    ...mediaGatewayData,
    mediagatewaynumlist: mediaGatewayData.mediagatewaynumlist.map((mediagatewaynum) => {
      return mediagatewaynum?.callingnum;
    })
  });

  return clonedMediaGatewayData;
}

export function adaptTemplateOptions(
  template: FormTemplate | undefined,
  networks: Network[]
): FormTemplate | undefined {
  const networkOptions = getNetworkOptions(networks);
  const clonedTemplate = cloneDeep(template);
  if (clonedTemplate) {
    setOptionForTemplateSectionField(clonedTemplate.main as FormTemplateSection, networkOptions, 'ownnetid');
  }
  return clonedTemplate;
}

function setAllFieldsAsReadOnlyExcludingFields(template: FormTemplate, excludingFields: string[]) {
  Object.entries(template).forEach(([_templateKey, templateEntry]) => {
    if (typeof templateEntry === 'string') {
      return;
    }
    Object.entries(templateEntry.fields).forEach(([fieldKey, fieldEntry]) => {
      if (!excludingFields.includes(fieldKey)) {
        fieldEntry.readOnly = true;
      }
    });
  });
}

function getNetworkOptions(fieldList: Network[]): FormTemplateOption[] {
  return fieldList.map((field) => {
    let label = `${field.networkId} - ${field.ownIPAddress}:${field.startPortNumber}`;
    const value = field.networkId;

    if (ipRegex.v6({ exact: true }).test(field.ownIPAddress)) {
      label = `${field.networkId} - [${field.ownIPAddress}]:${field.startPortNumber}`;
    }

    return {
      label,
      value,
    };
  });
}

export function setOptionForTemplateSectionField(
  templateSection: FormTemplateSection,
  options: FormTemplateOption[],
  fieldName: string,
): void {
  const { fields } = templateSection;

  if (!fields) {
    return;
  }

  const templateField = fields[fieldName];
  if (templateField == null || templateField?.options == null) {
    return;
  }

  setOptionFormTemplateField(templateField, options);
}

function setOptionFormTemplateField(
  templateField: FormTemplateField,
  options: FormTemplateOption[],
) {
  if (options.length > 0) {
    templateField.options = options;

    if (templateField.type !== 'advancedSelect' || templateField.placeholder == null) {
      // Only set the initial value for advancedSelect if the placeholder is empty.
      // All other types should still set the initial value.
      if (!templateField.preventInitialSelection) {
        templateField.initial = templateField.options?.[0].value ?? '';
      }
    }

  } else {
    templateField.initial = '';
  }
}
